import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Banknote, MoreHorizontal, Plus } from 'lucide-react'
import { useState } from 'react'
import { salariesQuery, useRetireSalary, useSetSalary, type Salary } from '@/api/payroll'
import { PayrollShell, type PayrollContext } from '@/app/payroll/payroll-shell'
import { wallOf } from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { formatMoney, parsePrice } from '@/domain/pass-rules'
import { monthEndText, monthStartText, salaryState } from '@/domain/payroll'
import { payrollErrorText } from '@/domain/payroll-errors'
import { addMonths, monthLabel, monthOf } from '@/domain/services'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { SelectField } from '@/ui/select'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/payroll/salaries')({
  component: Salaries,
})

function Salaries() {
  const { orgId } = Route.useParams()
  const [adding, setAdding] = useState(false)
  return (
    <PayrollShell
      orgId={orgId}
      tab="salaries"
      actions={(ctx) =>
        ctx.can.write ? (
          <Button variant="primary" icon={<Plus className="size-[18px]" />} onClick={() => setAdding(true)}>
            {t.payroll.salaries.add}
          </Button>
        ) : null
      }
    >
      {(ctx) => <SalariesScreen ctx={ctx} adding={adding} onAdding={setAdding} />}
    </PayrollShell>
  )
}

/// «Оклади» (specs/payroll-periods.md §2): every salary, the retired ones
/// too. A new one from a month closes the person's previous one the month
/// before (PS1); a wrong one is retired, never edited (PS2).
function SalariesScreen({ ctx, adding, onAdding }: { ctx: PayrollContext; adding: boolean; onAdding: (open: boolean) => void }) {
  const s = t.payroll.salaries
  const list = useQuery(salariesQuery(ctx.orgId))
  const retire = useRetireSalary(ctx.orgId)
  const [retiring, setRetiring] = useState<Salary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const thisMonth = monthOf(wallOf(new Date(), ctx.zone).date)

  if (list.isPending) return <LoadingBlock label={t.app.loading} />
  if (list.isError) {
    return <InlineBanner kind="error" message={errorText(list.error, payrollErrorText())} action={{ label: t.app.retry, onClick: () => void list.refetch() }} />
  }

  const rank = { current: 0, upcoming: 1, ended: 2, retired: 3 } as const
  const rows = [...list.data].sort(
    (a, b) =>
      rank[salaryState(a, thisMonth)] - rank[salaryState(b, thisMonth)] ||
      ctx.nameOf(a.userId).localeCompare(ctx.nameOf(b.userId), 'uk') ||
      b.fromMonth.localeCompare(a.fromMonth),
  )

  const columns: readonly DataColumn<Salary>[] = [
    {
      key: 'person',
      header: s.columns.person,
      width: 'minmax(0,1.4fr)',
      cell: (row) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={ctx.nameOf(row.userId)} src={ctx.photoOf(row.userId)} className="size-7" />
          <span className="truncate">{ctx.nameOf(row.userId)}</span>
        </span>
      ),
    },
    {
      key: 'amount',
      header: s.columns.amount,
      width: 'minmax(110px,0.7fr)',
      numeric: true,
      mobile: 'trailing',
      cell: (row) => <span className="font-medium text-text">{formatMoney(row.amountMinor)}</span>,
    },
    {
      key: 'span',
      header: s.columns.span,
      width: 'minmax(0,1fr)',
      cell: (row) => <span>{s.span(monthStartText(row.fromMonth), row.toMonth ? monthEndText(row.toMonth) : null)}</span>,
    },
    { key: 'state', header: s.columns.state, width: '160px', hideBelow: 'md', cell: (row) => s.state[salaryState(row, thisMonth)] },
    { key: 'comment', header: s.columns.comment, width: 'minmax(0,1fr)', hideBelow: 'lg', mobile: 'hidden', cell: (row) => row.comment ?? row.retireReason ?? '' },
  ]

  return (
    <div className="flex flex-col gap-4">
      {!ctx.can.write ? <InlineBanner kind="info" message={s.readOnly} /> : null}
      {rows.length === 0 ? (
        <EmptyState icon={<Banknote />} title={s.empty} message={s.emptyMessage} />
      ) : (
        <DataList
          label={t.payroll.tabs.salaries}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTone={(row) => (row.retiredAt || salaryState(row, thisMonth) === 'ended' ? 'muted' : 'normal')}
          rowActions={
            ctx.can.write
              ? (row) =>
                  row.retiredAt ? null : (
                    <RowMenu label={t.app.more} trigger={<MoreHorizontal className="size-4" />}>
                      <RowMenuItem destructive onSelect={() => setRetiring(row)}>
                        {s.retire}
                      </RowMenuItem>
                    </RowMenu>
                  )
              : undefined
          }
        />
      )}
      <p className="px-4 text-caption text-muted">{s.noProration}</p>

      <SalaryPanel ctx={ctx} open={adding} onClose={() => onAdding(false)} thisMonth={thisMonth} />
      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRetiring(null)
            setError(null)
          }
        }}
        title={s.retireTitle}
        message={retiring ? s.retireMessage(ctx.nameOf(retiring.userId), formatMoney(retiring.amountMinor)) : ''}
        confirmLabel={s.retire}
        busy={retire.isPending}
        error={error ? <InlineBanner kind="error" message={error} /> : undefined}
        onConfirm={() =>
          retiring &&
          retire.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (failure) => setError(errorText(failure, payrollErrorText())),
          })
        }
      />
    </div>
  )
}

/// «Новий оклад»: who, how much a month, from which month. Months from a
/// year back to a year ahead — a salary agreed late is set from the month it
/// was agreed for.
function SalaryPanel({ ctx, open, onClose, thisMonth }: { ctx: PayrollContext; open: boolean; onClose: () => void; thisMonth: string }) {
  const s = t.payroll.salaries
  const save = useSetSalary(ctx.orgId)
  const [userId, setUserId] = useState('')
  const [amount, setAmount] = useState('')
  const [fromMonth, setFromMonth] = useState(thisMonth)
  const [comment, setComment] = useState('')
  const [tried, setTried] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const amountMinor = parsePrice(amount)
  const months = Array.from({ length: 25 }, (_, index) => addMonths(thisMonth, 12 - index))
  const people = [...ctx.members].sort((a, b) => ctx.nameOf(a.userId).localeCompare(ctx.nameOf(b.userId), 'uk'))

  const reset = () => {
    setUserId('')
    setAmount('')
    setFromMonth(thisMonth)
    setComment('')
    setTried(false)
    setError(null)
  }
  const close = () => {
    reset()
    onClose()
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => (next ? undefined : close())}
      title={s.panelTitle}
      description={s.panelDescription}
      busy={save.isPending}
      onSubmit={(event) => {
        event.preventDefault()
        setTried(true)
        if (!userId || amountMinor === null || amountMinor <= 0) return
        setError(null)
        save.mutate(
          { userId, amountMinor, fromMonth, comment: comment.trim() || null },
          { onSuccess: close, onError: (failure) => setError(errorText(failure, payrollErrorText())) },
        )
      }}
      footer={
        <>
          <Button variant="quiet" onClick={close} disabled={save.isPending}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={save.isPending}>
            {s.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <SelectField
          label={s.person}
          value={userId}
          onChange={setUserId}
          placeholder={s.personPlaceholder}
          autoFocus
          error={tried && !userId ? s.personRequired : undefined}
          options={people.map((member) => ({
            value: member.userId,
            label: ctx.nameOf(member.userId),
            leading: <Avatar name={ctx.nameOf(member.userId)} src={ctx.photoOf(member.userId)} className="size-6" />,
          }))}
        />
        <Field
          label={s.amount}
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          error={tried && (amountMinor === null || amountMinor <= 0) ? s.amountRequired : undefined}
        />
        <SelectField
          label={s.from}
          value={fromMonth}
          onChange={setFromMonth}
          options={months.map((month) => ({ value: month, label: capital(monthLabel(month)) }))}
        />
        <Field label={s.comment} value={comment} maxLength={500} placeholder={s.commentPlaceholder} onChange={(event) => setComment(event.target.value)} />
        <p className="text-caption text-muted">{s.noProration}</p>
        {error ? <InlineBanner kind="error" message={error} /> : null}
      </div>
    </SidePanel>
  )
}

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)
